import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { UserRound, Stethoscope, Building2, ShieldCheck, UserCog, Globe, LogOut, Check, Briefcase, CalendarCheck, Package, ReceiptText, MonitorSmartphone, HardDrive } from "lucide-react";
import { getUser, logout, api, saveUser } from "../api";

const ROLE_ICON = { user: UserRound, professional: Stethoscope, organization: Building2, admin: ShieldCheck };
const LANGS = [["it", "IT"], ["en", "EN"], ["de", "DE"], ["fr", "FR"]];

export function AccountMenu() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const u = getUser();
  const [avatarUrl, setAvatarUrl] = useState(u?.avatar_url);

  // La login non include avatar_url: una tantum, se assente nel profilo salvato, recupera /auth/me
  useEffect(() => {
    if (!u || u.avatar_url !== undefined) return;
    api.get("/auth/me").then(({ data }) => {
      saveUser({ ...getUser(), avatar_url: data.avatar_url, cover_url: data.cover_url, profile_complete: data.profile_complete });
      setAvatarUrl(data.avatar_url);
    }).catch(() => {});
  }, []);

  // Avatar aggiornato da /settings: l'header riflette subito la nuova foto
  useEffect(() => {
    const onUpd = () => setAvatarUrl(getUser()?.avatar_url);
    window.addEventListener("user-updated", onUpd);
    return () => window.removeEventListener("user-updated", onUpd);
  }, []);

  // Chiusura: click/touch fuori, Esc (con ritorno focus al pulsante)
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") { setOpen(false); btnRef.current?.focus(); } };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc, { passive: true });
    document.addEventListener("keydown", onKey);
    menuRef.current?.querySelector("[data-menu-item]")?.focus();
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!u) return null;

  // Interfaccia reale di modifica protetta (endpoint /me: ownership implicita, nessun ID nell'URL)
  const editTo = "/settings";

  const onBtnKey = (e) => {
    if (["Enter", " ", "ArrowDown"].includes(e.key)) { e.preventDefault(); setOpen(true); }
  };
  const onMenuKey = (e) => {
    const items = Array.from(menuRef.current?.querySelectorAll("[data-menu-item]") || []);
    const idx = items.indexOf(document.activeElement);
    if (e.key === "ArrowDown") { e.preventDefault(); items[(idx + 1) % items.length]?.focus(); }
    else if (e.key === "ArrowUp") { e.preventDefault(); items[(idx - 1 + items.length) % items.length]?.focus(); }
    else if (e.key === "Home") { e.preventDefault(); items[0]?.focus(); }
    else if (e.key === "End") { e.preventDefault(); items[items.length - 1]?.focus(); }
    else if (e.key === "Tab") setOpen(false);
  };
  const change = (l) => { i18n.changeLanguage(l); localStorage.setItem("lang", l); };

  const Fallback = ROLE_ICON[u.role] || UserRound;
  return (
    <div className="yp-account" ref={rootRef}>
      <button ref={btnRef} className="yp-account-btn" data-testid="account-menu-button"
        aria-haspopup="menu" aria-expanded={open} aria-label={t("account.menu")}
        onClick={() => setOpen(o => !o)} onKeyDown={onBtnKey}>
        {avatarUrl
          ? <img src={avatarUrl} alt="" data-testid="account-avatar" />
          : <span className={`yp-account-fallback role-${u.role}`} data-testid="account-avatar-fallback"><Fallback size={20} strokeWidth={1.8} aria-hidden="true" /></span>}
      </button>
      {open && (
        <div className="yp-account-menu" role="menu" aria-label={t("account.menu")} ref={menuRef}
          onKeyDown={onMenuKey} data-testid="account-menu">
          <div className="px-3 py-2 text-xs text-slate-500 truncate" data-testid="account-menu-identity">{u.nickname || u.email}</div>
          <Link to={editTo} role="menuitem" data-menu-item className="yp-account-item" data-testid="account-edit-profile" onClick={() => setOpen(false)}>
            <UserCog size={16} strokeWidth={1.8} aria-hidden="true" />{t("account.edit_profile")}
          </Link>
          {/* R2-5.1/R2-6 UX: Professionista -> Amministrazione, PAM Drive, PAM Connect (spostate dalla barra superiore) */}
          {u.role === "professional" && (<>
            <Link to="/admin-pro" role="menuitem" data-menu-item className="yp-account-item" data-testid="account-admin-pro" onClick={() => setOpen(false)}>
              <Briefcase size={16} strokeWidth={1.8} aria-hidden="true" />{t("adminpro.nav")}
            </Link>
            <Link to="/pam-bill" role="menuitem" data-menu-item className="yp-account-item" data-testid="account-pambill" onClick={() => setOpen(false)}>
              <ReceiptText size={16} strokeWidth={1.8} aria-hidden="true" />PAM Bill
            </Link>
            <Link to="/drive" role="menuitem" data-menu-item className="yp-account-item" data-testid="account-pamdrive" onClick={() => setOpen(false)}>
              <HardDrive size={16} strokeWidth={1.8} aria-hidden="true" />{t("nav.drive")}
            </Link>
            <Link to="/pam-connect" role="menuitem" data-menu-item className="yp-account-item" data-testid="account-pamconnect" onClick={() => setOpen(false)}>
              <MonitorSmartphone size={16} strokeWidth={1.8} aria-hidden="true" />{t("nav.pamconnect")}
            </Link>
          </>)}
          {/* R2-5.1/R2-6 UX: Struttura -> Prenotazioni, Ordini, Amministrazione, PAM Drive, PAM Connect */}
          {u.role === "organization" && (<>
            <Link to="/bookings" role="menuitem" data-menu-item className="yp-account-item" data-testid="account-bookings" onClick={() => setOpen(false)}>
              <CalendarCheck size={16} strokeWidth={1.8} aria-hidden="true" />{t("nav.bookings")}
            </Link>
            <Link to="/orders" role="menuitem" data-menu-item className="yp-account-item" data-testid="account-orders" onClick={() => setOpen(false)}>
              <Package size={16} strokeWidth={1.8} aria-hidden="true" />{t("shop.orders")}
            </Link>
            <Link to="/admin-org" role="menuitem" data-menu-item className="yp-account-item" data-testid="account-admin-org" onClick={() => setOpen(false)}>
              <Briefcase size={16} strokeWidth={1.8} aria-hidden="true" />{t("adminpro.nav")}
            </Link>
            <Link to="/pam-bill" role="menuitem" data-menu-item className="yp-account-item" data-testid="account-pambill-org" onClick={() => setOpen(false)}>
              <ReceiptText size={16} strokeWidth={1.8} aria-hidden="true" />PAM Bill
            </Link>
            <Link to="/drive" role="menuitem" data-menu-item className="yp-account-item" data-testid="account-pamdrive-org" onClick={() => setOpen(false)}>
              <HardDrive size={16} strokeWidth={1.8} aria-hidden="true" />{t("nav.drive")}
            </Link>
            {u.clinical_enabled === true && <Link to="/pam-connect" role="menuitem" data-menu-item className="yp-account-item" data-testid="account-pamconnect-org" onClick={() => setOpen(false)}>
              <MonitorSmartphone size={16} strokeWidth={1.8} aria-hidden="true" />{t("nav.pamconnect")}
            </Link>}
          </>)}
          <div className="yp-account-sep" role="separator" />
          <div className="yp-account-item !cursor-default" aria-hidden="true">
            <Globe size={16} strokeWidth={1.8} />{t("account.language")}
          </div>
          <div className="yp-account-langs" role="group" aria-label={t("account.language")} data-testid="account-language-group">
            {LANGS.map(([k, v]) => (
              <button key={k} role="menuitemradio" aria-checked={i18n.language === k} data-menu-item
                className="yp-account-lang" data-testid={`account-lang-${k}`}
                onClick={() => { change(k); setOpen(false); btnRef.current?.focus(); }}>
                {v}{i18n.language === k && <Check size={12} className="inline ml-1 -mt-0.5" aria-hidden="true" />}
              </button>))}
          </div>
          <div className="yp-account-sep" role="separator" />
          <button role="menuitem" data-menu-item className="yp-account-item text-red-600" data-testid="account-logout" onClick={logout}>
            <LogOut size={16} strokeWidth={1.8} aria-hidden="true" />{t("account.logout")}
          </button>
        </div>)}
    </div>);
}
